// frontend/src/admin/LLMProviderManagement.js
import React, { useState, useEffect } from 'react';
import './llmprovidermanagement.css';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://80.225.197.31:8001';

const LLMProviderManagement = () => {
  const [providers, setProviders] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit config state
  const [editingProvider, setEditingProvider] = useState(null); // provider_name being edited
  const [editForm, setEditForm] = useState({ model: '', max_tokens: '', temperature: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const providersResponse = await fetch(`${API_BASE}/admin/llm-management/providers`);
      if (!providersResponse.ok) throw new Error('Failed to fetch providers');
      const providersData = await providersResponse.json();

      const dashboardResponse = await fetch(`${API_BASE}/admin/llm-management/dashboard`);
      if (!dashboardResponse.ok) throw new Error('Failed to fetch dashboard data');
      const dashboardData = await dashboardResponse.json();

      setProviders(providersData);
      setDashboard(dashboardData);
      setError('');
    } catch (error) {
      setError(`Failed to load data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const reloadProviders = async () => {
    try {
      await fetch(`${API_BASE}/admin/llm-management/reload`, { method: 'POST' });
    } catch (error) {
      console.error('Provider reload failed:', error);
    }
  };
  
  
  const toggleProvider = async (providerName) => {
    try {
      const response = await fetch(`${API_BASE}/admin/llm-management/providers/${providerName}/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to toggle provider');
      setSuccess(`${providerName} provider toggled successfully`);
      await reloadProviders()
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(`Failed to toggle provider: ${error.message}`);
    }
  };

  const setDefaultProvider = async (providerName) => {
    try {
      const response = await fetch(`${API_BASE}/admin/llm-management/providers/${providerName}/set-default`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to set default provider');
      setSuccess(`${providerName} set as default provider`);
      await reloadProviders()
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(`Failed to set default provider: ${error.message}`);
    }
  };

  const startEditing = (provider) => {
    setEditingProvider(provider.provider_name);
    setEditForm({
      model: provider.config_data?.model || '',
      max_tokens: provider.config_data?.max_tokens || '',
      temperature: provider.config_data?.temperature || '',
    });
  };

  const cancelEditing = () => {
    setEditingProvider(null);
    setEditForm({ model: '', max_tokens: '', temperature: '' });
  };

  const saveProviderConfig = async (providerName) => {
    try {
      const updates = {
        model: editForm.model,
        max_tokens: parseInt(editForm.max_tokens),
        temperature: parseFloat(editForm.temperature),
      };

      const response = await fetch(`${API_BASE}/admin/llm-management/providers/${providerName}/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (!response.ok) throw new Error('Failed to update provider config');

      setSuccess(`${providerName} configuration updated successfully`);
      setEditingProvider(null);
      await reloadProviders()
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(`Failed to update config: ${error.message}`);
    }
  };

  const getProviderIcon = (providerName) => {
    const icons = {
      'openai': '🤖',
      'gemini': '💎',
      'deepseek': '🧠',
      'openrouter': '🔄'
    };
    return icons[providerName.toLowerCase()] || '⚡';
  };

  const getStatusBadge = (provider) => {
    if (!provider.is_active) {
      return <span className="status-badge disabled">Disabled</span>;
    }
    if (provider.is_default) {
      return <span className="status-badge default">Default</span>;
    }
    return <span className="status-badge active">Active</span>;
  };

  const getApiStatusColor = (status) => {
    switch (status) {
      case 'available': return '#10b981';
      case 'error': return '#ef4444';
      case 'testing': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="llm-management-container">
        <div className="loading-spinner">Loading LLM provider data...</div>
      </div>
    );
  }

  return (
    <div className="llm-management-container">
      <div className="llm-management-header">
        <h1>🤖 LLM Provider Management</h1>
        <p>Manage AI language model providers and monitor usage statistics</p>
        <button className="refresh-btn" onClick={loadData}>
          🔄 Refresh Data
        </button>
      </div>

      {error && (
        <div className="alert alert-error">❌ {error}</div>
      )}
      {success && (
        <div className="alert alert-success">✅ {success}</div>
      )}

      {/* Dashboard Overview */}
      {dashboard && (
        <div className="dashboard-overview">
          <h2>📊 Today's Usage Overview</h2>
          <div className="overview-cards">
            <div className="overview-card">
              <div className="card-icon">🎯</div>
              <div className="card-content">
                <h3>{dashboard.total_tokens_today.toLocaleString()}</h3>
                <p>Total Tokens Today</p>
              </div>
            </div>
            <div className="overview-card">
              <div className="card-icon">📈</div>
              <div className="card-content">
                <h3>{dashboard.total_requests_today}</h3>
                <p>API Requests Today</p>
              </div>
            </div>
            <div className="overview-card">
              <div className="card-icon">👥</div>
              <div className="card-content">
                <h3>{dashboard.top_students?.length || 0}</h3>
                <p>Active Students</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Provider Management */}
      <div className="providers-section">
        <h2>⚙️ Provider Configuration</h2>

        <div className="providers-grid">
          {providers.map((provider) => (
            <div key={provider.provider_name} className={`provider-card ${!provider.is_active ? 'disabled' : ''}`}>
              <div className="provider-header">
                <div className="provider-title">
                  <span className="provider-icon">{getProviderIcon(provider.provider_name)}</span>
                  <h3>{provider.provider_name.charAt(0).toUpperCase() + provider.provider_name.slice(1)}</h3>
                </div>
                {getStatusBadge(provider)}
              </div>

              <div className="provider-stats">
                <div className="stat-row">
                  <span className="stat-label">API Status:</span>
                  <span className="api-status" style={{ color: getApiStatusColor(provider.api_status) }}>
                    ● {provider.api_status}
                  </span>
                </div>
                <div className="stat-row">
                  <span className="stat-label">Total Requests:</span>
                  <span className="stat-value">{provider.total_requests || 0}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-label">Success Rate:</span>
                  <span className="stat-value">
                    {provider.total_requests > 0
                      ? `${((provider.successful_requests || 0) / provider.total_requests * 100).toFixed(1)}%`
                      : 'N/A'}
                  </span>
                </div>
                <div className="stat-row">
                  <span className="stat-label">Avg Response:</span>
                  <span className="stat-value">
                    {provider.avg_response_time ? `${provider.avg_response_time.toFixed(0)}ms` : 'N/A'}
                  </span>
                </div>
                <div className="stat-row">
                  <span className="stat-label">Priority:</span>
                  <span className="stat-value">{provider.priority_order}</span>
                </div>
              </div>

              {/* Config Section - View or Edit */}
              <div className="provider-config">
                {editingProvider === provider.provider_name ? (
                  <div className="config-edit-form">
                    <div className="config-field">
                      <label>Model</label>
                      <input
                        type="text"
                        value={editForm.model}
                        onChange={(e) => setEditForm({ ...editForm, model: e.target.value })}
                        placeholder="e.g. gpt-4o-mini"
                      />
                    </div>
                    <div className="config-field">
                      <label>Max Tokens</label>
                      <input
                        type="number"
                        value={editForm.max_tokens}
                        onChange={(e) => setEditForm({ ...editForm, max_tokens: e.target.value })}
                        placeholder="e.g. 400"
                      />
                    </div>
                    <div className="config-field">
                      <label>Temperature</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="2"
                        value={editForm.temperature}
                        onChange={(e) => setEditForm({ ...editForm, temperature: e.target.value })}
                        placeholder="e.g. 0.7"
                      />
                    </div>
                    <div className="config-edit-actions">
                      <button className="save-btn" onClick={() => saveProviderConfig(provider.provider_name)}>
                        💾 Save
                      </button>
                      <button className="cancel-btn" onClick={cancelEditing}>
                        ✕ Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="config-details">
                    <div className="stat-row">
                      <span className="stat-label">Model:</span>
                      <span className="stat-value">{provider.config_data?.model || 'N/A'}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Max Tokens:</span>
                      <span className="stat-value">{provider.config_data?.max_tokens || 'N/A'}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Temperature:</span>
                      <span className="stat-value">{provider.config_data?.temperature || 'N/A'}</span>
                    </div>
                    <button className="edit-config-btn" onClick={() => startEditing(provider)}>
                      ✏️ Edit Config
                    </button>
                  </div>
                )}
              </div>

              <div className="provider-actions">
                <button
                  className={`toggle-btn ${provider.is_active ? 'disable' : 'enable'}`}
                  onClick={() => toggleProvider(provider.provider_name)}
                >
                  {provider.is_active ? '🔴 Disable' : '🟢 Enable'}
                </button>

                {provider.is_active && !provider.is_default && (
                  <button className="default-btn" onClick={() => setDefaultProvider(provider.provider_name)}>
                    ⭐ Set Default
                  </button>
                )}

                {provider.is_default && (
                  <span className="default-indicator">⭐ Current Default</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Students Today */}
      {dashboard?.top_students && dashboard.top_students.length > 0 && (
        <div className="top-students-section">
          <h2>🏆 Top Students (Token Usage)</h2>
          <div className="students-table">
            <table>
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Total Tokens</th>
                  <th>Total Requests</th>
                  <th>Est. Cost</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.top_students.slice(0, 10).map((student, index) => (
                  <tr key={student.student_id}>
                    <td>#{student.student_id}</td>
                    <td>{student.total_tokens.toLocaleString()}</td>
                    <td>{student.total_requests}</td>
                    <td>${(student.total_cost || 0).toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default LLMProviderManagement;